import React from 'react';
import { Link } from 'react-router-dom';
import EditorialArticle from '../../components/redesign/EditorialArticle.jsx';
import SEO from '../../components/SEO.jsx';
import { useCurrency } from '../../utils/currency.js';

const CALENDLY = 'https://calendly.com/aakeeo-technologies/crm-consultations';

const SERVICES = {
  implementation: {
    slug: 'implementation',
    num: 'I.',
    tag: 'SuiteCRM Implementation',
    eyebrow: '01 — Implementation',
    title: <>Most CRM projects fail. <em>Ours ship.</em></>,
    lead: "49% of CRM projects miss their deadline or get abandoned. The culprit is rarely the software — it's the approach. We've deployed SuiteCRM 100+ times across 20+ industries. Every one went live on schedule.",
    ctaPrimary: { label: 'Book a roadmap call', href: CALENDLY, external: true },
    ctaSecondary: { label: 'See Solutions →', to: '/services' },
    timeline: {
      head: "From kickoff to go-live in 5 weeks.",
      sub: "Not a vague 'it depends' timeline. Here's exactly what happens and when.",
      steps: [
        { week: 'Week 1', title: 'Discovery & data audit', desc: 'We map your current tools, data schema, team workflows, and pain points. You get a written implementation plan with zero ambiguity.' },
        { week: 'Week 2–3', title: 'Build & configure', desc: 'Server provisioning, module configuration, data migration, third-party integrations. Your CRM takes shape around your process — not a generic template.' },
        { week: 'Week 4', title: 'Train your team', desc: 'Hands-on workshops for sales, marketing, and ops. Role-specific training — not a one-size-fits-all webinar.' },
        { week: 'Week 5', title: 'Go live + support', desc: 'Controlled launch with real-time monitoring. We stay on call for 30 days post-launch to catch whatever your team encounters in the field.' },
      ],
    },
    grid: {
      head: 'Everything your CRM needs to earn revenue on day one.',
      items: [
        { title: 'Infrastructure that scales', desc: 'Deployed on your cloud of choice — AWS, DigitalOcean, or on-prem. Docker-containerised, Redis-cached, automated backups from day one.' },
        { title: 'Clean data migration', desc: 'Coming from spreadsheets or another CRM? We map your data, clean duplicates, validate every record, and deliver a migration audit report.' },
        { title: 'Modules that match your process', desc: 'Leads, Opportunities, Cases — configured to mirror how your team actually works. Your pipeline stages. Your custom fields. Your terminology.' },
        { title: 'Integrations wired in', desc: 'Tally, WhatsApp, Gmail, Razorpay, your ERP — connected during implementation, not as an afterthought. Your CRM talks to your stack from go-live.' },
        { title: 'Security & compliance built in', desc: 'Role-based access, field-level permissions, audit logging, two-factor auth, encryption at rest. Governance designed for your org structure.' },
        { title: 'Your team, trained and confident', desc: 'Role-specific workshops — sales gets pipeline training, ops gets reporting, admins get system management. Plus 30 days of post-launch support.' },
      ],
    },
    callout: {
      head: 'The cost of waiting',
      body: "Every week without a working CRM, your sales team spends 12+ hours on manual data entry, 30% of leads go untracked, and 1 in 4 follow-ups fall through. That's not an efficiency problem — it's a revenue leak.",
    },
  },

  customisation: {
    slug: 'customisation',
    num: 'II.',
    tag: 'SuiteCRM Customisation',
    eyebrow: '02 — Customisation',
    title: <>Off-the-shelf CRM <em>doesn&apos;t fit</em>.</>,
    lead: "Most CRMs ask you to bend. We don't. We build custom modules, workflows, logic hooks and layouts so the system mirrors the way your best people already work — not a vendor's idea of how they should.",
    ctaPrimary: { label: 'Start the conversation', href: CALENDLY, external: true },
    ctaSecondary: { label: 'See Solutions →', to: '/services' },
    compare: {
      head: 'Before and after.',
      rows: [
        { before: 'Spreadsheet-driven approvals', after: 'SuiteCRM workflows with audit trail' },
        { before: 'WhatsApp screenshots and copy-paste', after: 'Integrated records, every touchpoint logged' },
        { before: 'Weekly reports compiled by hand', after: 'Real-time dashboards for each role' },
        { before: 'Onboarding friction, ambiguity', after: 'Streamlined UI, clear next actions' },
      ],
    },
    grid: {
      head: 'The customisation surface.',
      items: [
        { title: 'Custom modules', desc: 'Entirely new record types and relationships — shipped as first-class citizens of your CRM, not plugins that break on upgrade.' },
        { title: 'Automated workflows', desc: 'Approvals, escalations, SLA tracking, and task routing coded against your rules. No low-code guesswork.' },
        { title: 'Custom dashboards', desc: 'Role-specific views so leadership, sales and ops each open their own version of the truth.' },
        { title: 'UI / UX tuned for speed', desc: "We strip clutter, surface the fields your team actually uses, and kill the clicks that don't earn their place." },
        { title: 'Cross-module reports', desc: 'Pull from leads, projects, finance and support into single dashboards. Finally, a board-ready view.' },
        { title: 'Permission architecture', desc: 'Field-level, record-level, role-level — built to survive an auditor, not just day-one scrutiny.' },
      ],
    },
    callout: {
      head: 'Upgrade-safe, always',
      body: "Every customisation we build is engineered to survive SuiteCRM version upgrades. We don't touch core files. If you upgrade without us, your workflows still ship.",
    },
  },

  integration: {
    slug: 'integration',
    num: 'III.',
    tag: 'CRM Integration',
    eyebrow: '03 — Integration',
    title: <>Your team shouldn&apos;t be the <em>integration layer</em>.</>,
    lead: "A CRM that doesn't talk to your ERP, your finance system, your phone tree or WhatsApp is a CRM half-used. We build reliable, observable integrations — not fragile Zapier chains — so your data lives in one place.",
    ctaPrimary: { label: 'Map your integration', href: CALENDLY, external: true },
    ctaSecondary: { label: 'See Solutions →', to: '/services' },
    grid: {
      head: 'What we wire up.',
      items: [
        { title: 'ERPs', desc: 'NetSuite, Odoo, SAP, Tally, ERPNext — two-way sync with auth, retries and observability.' },
        { title: 'Payment & invoicing', desc: 'Stripe, Razorpay, QuickBooks, Zoho Books — invoices, subscriptions, reconciliation.' },
        { title: 'Communications', desc: 'WhatsApp Business API, Twilio, Exotel, RingCentral — calls and messages logged into the CRM.' },
        { title: 'Email', desc: 'Gmail, Outlook, Mailgun, Sendgrid — true two-way sync, not just BCC drops.' },
        { title: 'Data warehouse', desc: 'Snowflake, BigQuery, Postgres — ETL and CDC for the reporting your CFO actually wants.' },
        { title: 'Custom APIs', desc: 'With auth, retries, observability and real alerting — not fire-and-forget webhooks.' },
      ],
    },
    process: {
      head: 'How we ship integrations.',
      steps: [
        { n: '01', title: 'Audit', desc: 'Map data flow, rate limits, failure modes. Write the integration spec you can hand to an auditor.' },
        { n: '02', title: 'Build & test', desc: 'Staged environment first. Synthetic and real-data tests. Observability baked in before go-live.' },
        { n: '03', title: 'Monitor & maintain', desc: 'Health checks, rate-limit alerts, error budgets. If an API changes, you hear from us before your users do.' },
      ],
    },
    callout: {
      head: 'Why n8n, not Zapier',
      body: 'Open source, self-hosted, no per-task pricing, and you own the workflow definitions. We use it in production for clients running 10,000+ tasks/hour.',
    },
  },

  ai: {
    slug: 'ai',
    num: 'IV.',
    tag: 'AI Workflow Automation',
    eyebrow: '04 — AI Workflow Automation',
    title: <>AI that stays <em>inside your system of record</em>.</>,
    lead: "Most AI CRM add-ons ship your pipeline to a third party. We design scoring, routing and follow-up automations that run on your infrastructure — or against models you control. Your data stays yours.",
    ctaPrimary: { label: 'Find your automation wins', href: CALENDLY, external: true },
    ctaSecondary: { label: 'See Solutions →', to: '/services' },
    stats: {
      head: 'One recent engagement.',
      items: [
        { v: '80%', l: 'manual work removed', d: 'Lead enrichment, routing and follow-up replies generated inside the CRM.' },
        { v: '15 min', l: 'average lead response', d: 'Down from 6+ hours. Automated first-touch on every inbound.' },
        { v: '+23%', l: 'close rate improvement', d: 'Better lead scoring and faster response, measured over 90 days.' },
      ],
    },
    grid: {
      head: 'Where AI actually earns its keep.',
      items: [
        { title: 'Lead scoring', desc: 'Fit and intent models trained on your won/lost data. Scores live inside CRM fields you can query.' },
        { title: 'Follow-up generation', desc: 'Tailored drafts from your CRM data and a template library your reps approve before send.' },
        { title: 'Data entry elimination', desc: 'Meeting notes and call summaries written straight into the record, with source audit trails.' },
        { title: 'Smart alerts', desc: 'Pattern-detection on pipeline changes — pings your team when a deal starts to slip.' },
        { title: 'Automated reporting', desc: 'Narrative summaries of the week, the month, the quarter — not just tables.' },
        { title: 'Natural-language queries', desc: 'Ask your CRM a question. Get an answer grounded in your data, with links to the records.' },
      ],
    },
    callout: {
      head: 'Three ways it stays yours',
      body: 'Self-hosted LLMs. BYO-API-key. Full audit trail — every AI-written field is traceable back to prompt and context. No data ever trains a public model.',
    },
  },

  support: {
    slug: 'support',
    num: 'V.',
    tag: 'Support & Maintenance',
    eyebrow: '05 — Support & Maintenance',
    title: <>The engineering team behind your CRM, <em>on retainer</em>.</>,
    lead: "Open source without a steward is a liability. With us on retainer you get upgrades, security patches, new feature work and a predictable monthly — not another subscription that grows with every seat.",
    ctaPrimary: { label: 'Book a health check', href: CALENDLY, external: true },
    ctaSecondary: { label: 'See Solutions →', to: '/services' },
    callout: {
      head: 'The cost of CRM downtime',
      body: '50 users × 8 hours of lost productivity ≈ ₹2.4L — per outage. A competent retainer is an order of magnitude cheaper than a single bad week.',
    },
    grid: {
      head: "What's on the plan.",
      items: [
        { title: 'Version upgrades', desc: 'Regression-tested SuiteCRM 7 → 8 and point-release upgrades on a schedule you agree.' },
        { title: 'Security patches', desc: 'CVE monitoring, emergency patching, and quarterly hardening reviews.' },
        { title: 'Performance tuning', desc: 'Indexing, query optimisation, Elasticsearch, Redis — we diagnose and fix the 95th percentile.' },
        { title: 'Bug fix SLA', desc: 'Named engineer, documented response windows, clear escalation paths.' },
        { title: 'Proactive monitoring', desc: 'Uptime, error rates, job queues, integration health — alerts before your users notice.' },
        { title: 'Direct engineer access', desc: 'No tier-1 scripts. No ticket triage theatre. You talk to the person building your CRM.' },
      ],
    },
    pricing: {
      head: 'From ₹1.5L / month.',
      sub: 'Month-to-month. Less than the cost of a junior developer. Monthly audits, quarterly reviews, and a real engineer on the other end.',
    },
  },
};

export function ServiceSubpageA({ slug, onNav }) {
  const { price } = useCurrency();
  const base = SERVICES[slug];
  if (!base) return null;

  // Region-aware price swaps for Support (others keep their static copy).
  const s = slug === 'support'
    ? {
        ...base,
        callout: {
          ...base.callout,
          body: `50 users × 8 hours of lost productivity ≈ ${price({ usd: '$2.9k', inr: '₹2.4L' })} — per outage. A competent retainer is an order of magnitude cheaper than a single bad week.`,
        },
        pricing: {
          ...base.pricing,
          head: `From ${price({ usd: '$1.8k', inr: '₹1.5L' })} / month.`,
        },
      }
    : base;

  return (
    <EditorialArticle
      navPage="solutions"
      onNav={onNav}
      eyebrow={s.eyebrow}
      title={s.title}
      lead={s.lead}
      primaryCta={
        s.ctaPrimary && (
          <a className="btnA-primary" href={s.ctaPrimary.href} target="_blank" rel="noopener noreferrer">
            {s.ctaPrimary.label}
          </a>
        )
      }
      secondaryCta={
        s.ctaSecondary && (
          <Link to={s.ctaSecondary.to} className="btnA-ghost">{s.ctaSecondary.label}</Link>
        )
      }
    >
      <SEO
        title={`${s.tag} | Aakeeo`}
        description={typeof s.lead === 'string' ? s.lead : undefined}
        keywords={[s.tag]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Solutions', url: '/services' },
          { name: s.tag },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: s.tag,
          provider: { '@type': 'Organization', name: 'Aakeeo Technologies', url: 'https://www.aakeeo.com' },
          areaServed: ['India', 'UK', 'US', 'Australia', 'France'],
          description: typeof s.lead === 'string' ? s.lead : undefined,
        }}
      />

      {s.timeline && (
        <section>
          <div className="procA-head" style={{ marginBottom: 40 }}>
            <span className="eyebrowA">{s.timeline.head}</span>
            <h2>{s.timeline.sub}</h2>
          </div>
          <ol className="procA-steps" style={{ gridTemplateColumns: `repeat(${s.timeline.steps.length}, 1fr)` }}>
            {s.timeline.steps.map((step) => (
              <li key={step.week}>
                <span className="procA-num">{step.week}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {s.compare && (
        <section>
          <h2 className="serviceA-h2">{s.compare.head}</h2>
          <div className="serviceA-compare">
            <div className="serviceA-compare-head">
              <span>Before</span>
              <span>After</span>
            </div>
            {s.compare.rows.map((r, i) => (
              <div key={i} className="serviceA-compare-row">
                <span className="serviceA-compare-before">{r.before}</span>
                <span className="serviceA-compare-after">{r.after}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {s.stats && (
        <section>
          <h2 className="serviceA-h2">{s.stats.head}</h2>
          <div className="serviceA-stats">
            {s.stats.items.map((it) => (
              <div key={it.l} className="serviceA-stat">
                <strong>{it.v}</strong>
                <span>{it.l}</span>
                <p>{it.d}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {s.grid && (
        <section>
          <h2 className="serviceA-h2">{s.grid.head}</h2>
          <div className="pillA-grid pillA-grid--3">
            {s.grid.items.map((it, i) => (
              <div key={i} className="pillA-card">
                <h3>{it.title}</h3>
                <p>{it.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {s.process && (
        <section>
          <h2 className="serviceA-h2">{s.process.head}</h2>
          <ol className="procA-steps" style={{ gridTemplateColumns: `repeat(${s.process.steps.length}, 1fr)` }}>
            {s.process.steps.map((step) => (
              <li key={step.n}>
                <span className="procA-num">{step.n}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {s.callout && (
        <section>
          <div className="serviceA-callout">
            <span className="eyebrowA">{s.callout.head}</span>
            <p>{s.callout.body}</p>
          </div>
        </section>
      )}

      {s.pricing && (
        <section>
          <div className="serviceA-pricing">
            <h2>{s.pricing.head}</h2>
            <p>{s.pricing.sub}</p>
            <a className="btnA-primary" href={CALENDLY} target="_blank" rel="noopener noreferrer">
              Talk about a retainer
            </a>
          </div>
        </section>
      )}

      <section className="ctaA" style={{ padding: 0, marginTop: 48 }}>
        <div className="ctaA-inner">
          <h2>Let&apos;s scope your {s.tag.toLowerCase()}.</h2>
          <p>30 minutes. No slide deck. We&apos;ll map what you have, flag the risks, and tell you whether we&apos;re the right fit.</p>
          <div className="ctaA-buttons">
            <a className="btnA-primary" href={CALENDLY} target="_blank" rel="noopener noreferrer">Book a strategy call</a>
            <Link to="/services" className="btnA-ghost">← Back to Solutions</Link>
          </div>
        </div>
      </section>
    </EditorialArticle>
  );
}
